/**
 * AES-CBC encryption / decryption helpers built on the browser's
 * Web Crypto API (`crypto.subtle`). Everything runs locally in the
 * browser — no key, IV, or plaintext ever leaves the page.
 */

export const BYTE_ENCODINGS = ["utf-8", "hex", "base64"] as const;
export type ByteEncoding = (typeof BYTE_ENCODINGS)[number];

export const OUTPUT_ENCODINGS = ["base64", "hex"] as const;
export type OutputEncoding = (typeof OUTPUT_ENCODINGS)[number];

export const ENCODING_LABELS: Record<string, string> = {
  "utf-8": "UTF-8",
  hex: "Hex",
  base64: "Base64",
};

const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder();

/** Valid AES key sizes in bytes → 128 / 192 / 256-bit. */
const VALID_KEY_LENGTHS = [16, 24, 32];

/** Required AES-CBC IV size in bytes. */
const IV_LENGTH = 16;

// ---------------------------------------------------------------------------
// Encoding helpers
// ---------------------------------------------------------------------------

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.trim().replace(/^0x/i, "").replace(/\s+/g, "");
  if (clean.length === 0) return new Uint8Array(0);
  if (clean.length % 2 !== 0) {
    throw new Error("Hex must have an even number of characters.");
  }
  if (!/^[0-9a-fA-F]+$/.test(clean)) {
    throw new Error("Hex contains invalid characters (expected 0-9, a-f).");
  }
  const bytes = new Uint8Array(clean.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function base64ToBytes(b64: string): Uint8Array {
  let binary: string;
  try {
    binary = atob(b64.trim());
  } catch {
    throw new Error("Invalid Base64 string.");
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Decode a user-entered string into raw bytes using the given encoding. */
export function decodeToBytes(value: string, encoding: ByteEncoding): Uint8Array {
  if (encoding === "hex") return hexToBytes(value);
  if (encoding === "base64") return base64ToBytes(value);
  return utf8Encoder.encode(value); // utf-8
}

/** Encode raw bytes into a display string (Base64 or Hex). */
export function encodeBytes(bytes: Uint8Array, encoding: OutputEncoding): string {
  if (encoding === "hex") return bytesToHex(bytes);
  return bytesToBase64(bytes); // base64
}

/** Number of bytes a value decodes to, or `null` if it is malformed. */
export function safeByteLength(value: string, encoding: ByteEncoding): number | null {
  try {
    return decodeToBytes(value, encoding).length;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// AES-CBC core
// ---------------------------------------------------------------------------

async function importAesKey(
  keyBytes: Uint8Array,
  usage: "encrypt" | "decrypt",
): Promise<CryptoKey> {
  if (!VALID_KEY_LENGTHS.includes(keyBytes.length)) {
    throw new Error(
      `AES key must be 16, 24, or 32 bytes (128/192/256-bit). Got ${keyBytes.length}.`,
    );
  }
  return crypto.subtle.importKey("raw", keyBytes, { name: "AES-CBC" }, false, [
    usage,
  ]);
}

function requireIv(ivBytes: Uint8Array): Uint8Array {
  if (ivBytes.length !== IV_LENGTH) {
    throw new Error(`AES-CBC IV must be exactly 16 bytes. Got ${ivBytes.length}.`);
  }
  return ivBytes;
}

export interface EncryptInput {
  plaintext: string;
  key: string;
  keyEncoding: ByteEncoding;
  iv: string;
  ivEncoding: ByteEncoding;
  outputEncoding: OutputEncoding;
}

/** Encrypt UTF-8 `plaintext` with AES-CBC, returning encoded ciphertext. */
export async function aesEncrypt(input: EncryptInput): Promise<string> {
  const keyBytes = decodeToBytes(input.key, input.keyEncoding);
  const ivBytes = requireIv(decodeToBytes(input.iv, input.ivEncoding));
  const key = await importAesKey(keyBytes, "encrypt");

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-CBC", iv: ivBytes },
    key,
    utf8Encoder.encode(input.plaintext),
  );

  return encodeBytes(new Uint8Array(ciphertext), input.outputEncoding);
}

export interface DecryptInput {
  ciphertext: string;
  ciphertextEncoding: OutputEncoding;
  key: string;
  keyEncoding: ByteEncoding;
  iv: string;
  ivEncoding: ByteEncoding;
}

/** Decrypt AES-CBC ciphertext back to the original UTF-8 plaintext. */
export async function aesDecrypt(input: DecryptInput): Promise<string> {
  const keyBytes = decodeToBytes(input.key, input.keyEncoding);
  const ivBytes = requireIv(decodeToBytes(input.iv, input.ivEncoding));
  const key = await importAesKey(keyBytes, "decrypt");
  const cipherBytes = decodeToBytes(input.ciphertext, input.ciphertextEncoding);

  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: "AES-CBC", iv: ivBytes },
      key,
      cipherBytes,
    );
  } catch {
    throw new Error(
      "Decryption failed — check the key, IV, and ciphertext encoding.",
    );
  }

  return utf8Decoder.decode(plaintext);
}

/** Cryptographically random bytes, returned in the chosen encoding. */
export function randomEncoded(length: number, encoding: OutputEncoding): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return encodeBytes(bytes, encoding);
}
